<template>
  <div class="irp-partner-data-section" data-testid="irp-partner-data-section">
    <IRPPartnerFilterBar
      :is-expanded="isEveryRowOpen"
      :matched-partner-uuids="matchedPartnerUuids"
      @toggle-expansion="setAllExpanded"
    />

    <IRPRecommendedPartnersTableSkeleton v-if="state.isLoading" />
    <BittsEmptyState
      v-else-if="hasError"
      :font-awesome-icon="ERROR_STATE.icon"
      :title="ERROR_STATE.title"
      :description="ERROR_STATE.description"
      :button="RETRY_BUTTON"
      data-testid="irp-partner-data-section-error-state"
      @button-clicked="refetch"
    />
    <BittsEmptyState
      v-else-if="!partners.length"
      :font-awesome-icon="emptyIcon"
      title="No partner data"
      :description="emptyDescription"
      data-testid="irp-partner-data-section-empty-state"
    />
    <div v-else class="irp-partner-data-section__container">
      <IRPPartnerRow
        v-for="(partner, index) in partners"
        :key="rowKey(partner, index)"
        :partner="partner"
        :columns="HEADER_COLUMNS[section]"
        :is-open="isRowOpen(partner, index)"
        @update:is-open="(isOpen) => setRowOpen(rowKey(partner, index), isOpen)"
      >
        <slot :partner="partner" />
      </IRPPartnerRow>
    </div>
    <BittsPaginator
      v-if="isPaginatorVisible"
      class="justify-end"
      :page="state.page"
      :last-page="lastPage"
      @page-click="setPage"
    />
  </div>
</template>

<script setup lang="ts" generic="Section extends RecordSectionType">
import { BittsEmptyState, BittsPaginator } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { isEqual } from 'lodash';
import { computed, provide, readonly, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { defaultErrorMessage } from '@/errors';
import { ProvidedAllFieldsExpansion } from '@/injectionKeys/individual_record';
import { useIndividualRecordStore } from '@/stores';
import type {
  RecordPartnerType,
  RecordSectionType,
} from '@/types/individual_record';

import { EXPAND_ALL_QUERY } from './irp';
import type { IRPPartnerColumnType } from './irp';
import IRPPartnerFilterBar from './IRPPartnerFilterBar.vue';
import IRPPartnerRow from './IRPPartnerRow.vue';
import IRPRecommendedPartnersTableSkeleton from './IRPRecommendedPartnersTableSkeleton.vue';
import { filtersFromQuery, hasFilterQueryChanged } from './recordFilterQuery';

const { section } = defineProps<{
  section: Section;
}>();

defineSlots<{
  default(props: { partner: RecordPartnerType<Section> }): unknown;
}>();

const EMPTY_DESCRIPTIONS: Record<RecordSectionType, string> = {
  master: 'No partner shares data on this record.',
  opportunities: 'No partner shares opportunities on this record.',
  contacts: 'No partner shares contacts on this record.',
};

const EMPTY_ICONS: Record<RecordSectionType, BittsIcon> = {
  master: ['fak', 'company'],
  opportunities: ['fak', 'opportunity'],
  contacts: ['fak', 'contacts'],
};

const HEADER_COLUMNS: Record<RecordSectionType, IRPPartnerColumnType[]> = {
  master: ['score', 'owner', 'populations'],
  opportunities: ['score', 'opportunities'],
  contacts: ['score', 'contacts'],
};

const LEAD_ICON: BittsIcon = ['fak', 'person'];

const ERROR_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'Partner data could not load',
  description: defaultErrorMessage,
  icon: ['fak', 'warning'],
};

const RETRY_BUTTON = {
  text: 'Try again',
  type: 'neutral',
  variant: 'outline',
} as const;

const individualRecordStore = useIndividualRecordStore();

const state = computed(() => individualRecordStore.sections[section]);
const partners = computed(
  () => state.value.partners.items as RecordPartnerType<Section>[],
);
const pagination = computed(() => state.value.partners.pagination);
const lastPage = computed(() => pagination.value.last_page ?? 1);
const hasMultiplePages = computed(() => lastPage.value > 1);
const hasError = computed(() => !!state.value.error);
const isPaginatorVisible = computed(
  () =>
    hasMultiplePages.value &&
    !hasError.value &&
    (state.value.isLoading || !!partners.value.length),
);

const emptyDescription = computed(() => EMPTY_DESCRIPTIONS[section]);
const emptyIcon = computed<BittsIcon>(() =>
  section === 'master' && individualRecordStore.mdmType === 'lead'
    ? LEAD_ICON
    : EMPTY_ICONS[section],
);

const matchedPartnerUuids = computed(() =>
  partners.value.map((partner) => partner.partner_organization_uuid),
);

const route = useRoute();

const isAllExpanded = ref(route.query.expand === EXPAND_ALL_QUERY.expand);
const expansionGeneration = ref(0);
const openOverrides = ref<Record<string, boolean>>({});

function rowKey(partner: RecordPartnerType<Section>, index: number) {
  return `${partner.partner_organization_uuid}_${index}`;
}

function isRowOpen(partner: RecordPartnerType<Section>, index: number) {
  return openOverrides.value[rowKey(partner, index)] ?? isAllExpanded.value;
}

const isEveryRowOpen = computed(
  () =>
    !!partners.value.length &&
    partners.value.every((partner, index) => isRowOpen(partner, index)),
);

function clearOverrides() {
  openOverrides.value = {};
}

function setRowOpen(key: string, isOpen: boolean) {
  openOverrides.value[key] = isOpen;
}

provide(ProvidedAllFieldsExpansion, {
  isExpanded: readonly(isAllExpanded),
  generation: readonly(expansionGeneration),
});

function setAllExpanded(isExpanded: boolean) {
  isAllExpanded.value = isExpanded;
  expansionGeneration.value += 1;
  clearOverrides();
}

function setPage(page: number) {
  if (state.value.isLoading) return;
  clearOverrides();
  return individualRecordStore.setPage(section, page);
}

function refetch() {
  return individualRecordStore.fetchSection(section, { isForced: true });
}

watch(
  () => route.query,
  (query, previousQuery) => {
    if (!hasFilterQueryChanged(query, previousQuery)) return;
    clearOverrides();
    individualRecordStore.setFilters(section, filtersFromQuery(query));
  },
);

const urlFilters = filtersFromQuery(route.query);
if (!isEqual(urlFilters, individualRecordStore.sections[section].filters)) {
  individualRecordStore.setFilters(section, urlFilters);
}
individualRecordStore.fetchSection(section);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-partner-data-section {
  @apply flex flex-col gap-16;
}

.irp-partner-data-section__container {
  @apply flex flex-col gap-4 rounded-16 border border-neutral-border p-8;
}

.irp-partner-data-section__container :deep(.irp-partner-row:nth-child(even)) {
  @apply bg-neutral-background-weak;
}
</style>
